import { listContenidoItems } from './contenidoOrden';
import { pathKeyFromIndices } from './temarioTree';
import { buildCampusNavHref, buildCampusTestNavHref } from './studentNavPaths';
import { displayArchivoNombre } from './archivoDisplayName';
import { getCourseFileObjectKey } from './signedCourseFileUrl';
import {
  getContenidoNodesForTab,
  normalizeCourseContenido,
  resolvePublishedCourseTabsAndContenido,
} from './normalizeCourseShape';
import {
  listLinkedTestTitles,
  temarioChildDuplicatesLinkedTests,
  temarioNodeHasVisibleContent,
} from './campusVisibleContent';
import { mergeTestStructureIntoCampusCourse } from './studentTestCampusContent';
import { applyCatalogTestsToTabTree, resolveCourseTestsCatalog } from './filterTemarioTestsForTab';
import {
  filterMatchingTestBlocks,
  getStudentTestBlockName,
  getStudentTestTopicName,
} from './studentTestBlocks';

/** @typedef {'tab'|'contenido-node'|'contenido-file'|'test-block'|'test-topic'} SidebarTreeNodeKind */

/**
 * @typedef {object} SidebarTreeNode
 * @property {string} id
 * @property {string} label
 * @property {SidebarTreeNodeKind} kind
 * @property {string} href
 * @property {SidebarTreeNode[]} children
 */

/**
 * @typedef {import("./temarioTree").TemarioNodo} TemarioNodo
 * @typedef {{ nombre?: string, temas?: Array<{ nombre?: string }>, id?: string }} TestBlockShape
 */

function isTestOriginNode(node) {
  return typeof node?.id === 'string' && node.id.startsWith('test-block-');
}

/**
 * @param {TemarioNodo} node
 * @param {string} pestana
 * @param {number[]} path
 * @returns {SidebarTreeNode | null}
 */
function buildContenidoSidebarNode(node, pestana, path) {
  if (!temarioNodeHasVisibleContent(node)) {
    return null;
  }
  const pathKey = pathKeyFromIndices(path);
  const lastIndex = path[path.length - 1] ?? 0;
  const label = typeof node?.nombre === 'string' && node.nombre.trim().length > 0
    ? node.nombre.trim()
    : `Elemento ${lastIndex + 1}`;
  const hijos = Array.isArray(node?.hijos) ? node.hijos : [];
  const linkedTestTitles = listLinkedTestTitles(node);

  const unitChildren = hijos.flatMap((hijo, childIndex) => {
    if (temarioChildDuplicatesLinkedTests(hijo, linkedTestTitles)) {
      return [];
    }
    const child = buildContenidoSidebarNode(hijo, pestana, [...path, childIndex]);
    return child ? [child] : [];
  });
  const resourceItems = listContenidoItems(node);
  const resourceChildren = resourceItems.map((item) => {
    if (item.kind === "test") {
      const titulo = typeof item.testRef?.titulo === "string" ? item.testRef.titulo.trim() : `Test ${item.sourceIndex + 1}`;
      const bloque = typeof item.testRef?.bloque === "string" && item.testRef.bloque.trim()
        ? item.testRef.bloque.trim()
        : pestana;
      return {
        id: `contenido-test-${pathKey}-${item.sourceIndex}`,
        label: titulo,
        kind: "test-topic",
        href: buildCampusTestNavHref(pestana, bloque, titulo),
        children: [],
      };
    }
    const archivo = item.archivo;
    const fileKey = getCourseFileObjectKey(archivo);
    const fileLabel = displayArchivoNombre(archivo, `Archivo ${item.sourceIndex + 1}`);
    const fileParams = new URLSearchParams();
    fileParams.set("pestana", pestana);
    fileParams.set("nav", pathKey);
    if (fileKey.length > 0) {
      fileParams.set("archivo", fileKey);
    }
    return {
      id: `contenido-file-${pathKey}-${item.sourceIndex}`,
      label: fileLabel,
      kind: "contenido-file",
      href: `/usuario/campus?${fileParams.toString()}`,
      children: [],
    };
  });

  return {
    id: `contenido-node-${pathKey}`,
    label,
    kind: 'contenido-node',
    href: buildCampusNavHref(pestana, path),
    children: [...resourceChildren, ...unitChildren],
  };
}

/**
 * @param {TestBlockShape[]} estructura
 * @param {string} pestana
 * @returns {SidebarTreeNode[]}
 */
function buildTestSidebarNodes(estructura, pestana) {
  if (!Array.isArray(estructura)) {
    return [];
  }

  const blocks = filterMatchingTestBlocks(estructura, pestana, false);

  return blocks.map((block, blockIndex) => {
    const blockName = getStudentTestBlockName(block, blockIndex);
    const temas = Array.isArray(block?.temas) ? block.temas : [];
    const topicChildren = temas.map((tema, topicIndex) => {
      const topicName = getStudentTestTopicName(tema, topicIndex);
      return {
        id: `test-topic-${blockName}-${topicName}`,
        label: topicName,
        kind: 'test-topic',
        href: buildCampusTestNavHref(pestana, blockName, topicName),
        children: [],
      };
    });

    return {
      id: `test-block-${blockName}`,
      label: blockName,
      kind: 'test-block',
      href: buildCampusTestNavHref(pestana, blockName),
      children: topicChildren,
    };
  });
}

/**
 * @param {TemarioNodo} node
 * @param {string} pestana
 * @returns {SidebarTreeNode[]}
 */
function buildTestSidebarNodesFromMergedBlock(node, pestana) {
  const blockName = typeof node?.nombre === 'string' && node.nombre.trim()
    ? node.nombre.trim()
    : 'Bloque';
  const temas = Array.isArray(node?.hijos) ? node.hijos : [];
  const topicChildren = temas
    .filter((tema) => temarioNodeHasVisibleContent(tema))
    .map((tema) => {
      const topicName = typeof tema?.nombre === 'string' && tema.nombre.trim()
        ? tema.nombre.trim()
        : 'Tema';
      return {
        id: `test-topic-${blockName}-${topicName}`,
        label: topicName,
        kind: 'test-topic',
        href: buildCampusTestNavHref(pestana, blockName, topicName),
        children: [],
      };
    });

  return [{
    id: typeof node?.id === 'string' && node.id ? node.id : `test-block-${blockName}`,
    label: blockName,
    kind: 'test-block',
    href: buildCampusTestNavHref(pestana, blockName),
    children: topicChildren,
  }];
}

/**
 * @param {TemarioNodo[]} nodes
 * @param {string} pestana
 * @param {TestBlockShape[]} testEstructura
 * @returns {SidebarTreeNode[]}
 */
function buildTabSidebarChildren(nodes, pestana, testEstructura) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return buildTestSidebarNodes(testEstructura, pestana);
  }

  /** @type {SidebarTreeNode[]} */
  const children = [];
  nodes.forEach((node, index) => {
    if (isTestOriginNode(node)) {
      children.push(...buildTestSidebarNodesFromMergedBlock(node, pestana));
      return;
    }
    const child = buildContenidoSidebarNode(node, pestana, [index]);
    if (child) {
      children.push(child);
    }
  });
  return children;
}

function collectExpandedTestNodeIds(tabNode, bloque, tema) {
  const expanded = [];
  if (!bloque) {
    return expanded;
  }

  const blockNode = tabNode.children.find(
    (child) => (child.kind === 'test-block' || child.kind === 'contenido-node')
      && child.label === bloque,
  );
  if (!blockNode) {
    return expanded;
  }

  expanded.push(blockNode.id);
  if (!tema) {
    return expanded;
  }

  const topicNode = blockNode.children.find(
    (child) => (child.kind === 'test-topic' || child.kind === 'contenido-node')
      && child.label === tema,
  );
  if (topicNode) {
    expanded.push(topicNode.id);
  }
  return expanded;
}

/**
 * @param {object} params
 * @param {string[]} params.pestanasCurso
 * @param {Record<string, TemarioNodo[]>} params.contenido
 * @param {TestBlockShape[]} params.testEstructura
 * @param {object[] | null | undefined} [params.courseTests]
 * @returns {SidebarTreeNode[]}
 */
export function buildStudentSidebarTree({
  pestanasCurso,
  contenido,
  testEstructura,
  courseTests,
}) {
  const normalizedContenido = normalizeCourseContenido(contenido || {});
  const published = resolvePublishedCourseTabsAndContenido(pestanasCurso, normalizedContenido);
  const tabs = published.pestanasCurso;
  const merged = mergeTestStructureIntoCampusCourse(
    { pestanasCurso: tabs, contenido: published.contenido },
    Array.isArray(testEstructura) ? testEstructura : [],
    tabs,
  );

  return merged.pestanasCurso.map((tabLabel) => {
    const contenidoNodes = applyCatalogTestsToTabTree(
      getContenidoNodesForTab(merged.contenido, tabLabel),
      resolveCourseTestsCatalog(courseTests, testEstructura),
      tabLabel,
      tabs,
    );
    const children = buildTabSidebarChildren(
      contenidoNodes,
      tabLabel,
      Array.isArray(testEstructura) ? testEstructura : [],
    );

    const href = children.some((child) => child.kind === 'test-block' || child.kind === 'test-topic')
      ? buildCampusTestNavHref(tabLabel)
      : buildCampusNavHref(tabLabel);

    return {
      id: `tab-${tabLabel}`,
      label: tabLabel,
      kind: 'tab',
      href,
      children,
    };
  });
}

/**
 * Collect node ids that should stay expanded for the current location.
 * @param {SidebarTreeNode[]} tree
 * @param {{ pathname: string, search: string }} location
 * @returns {string[]}
 */
export function collectExpandedSidebarNodeIds(tree, location) {
  const params = new URLSearchParams(typeof location.search === 'string' ? location.search : '');
  const pestana = (params.get('pestana') || '').trim();
  const navPath = (params.get('nav') || '').trim();
  const bloque = (params.get('bloque') || '').trim();
  const tema = (params.get('tema') || '').trim();
  const expanded = [];

  if (pestana.length === 0) {
    return expanded;
  }

  const tabNode = tree.find((node) => node.kind === 'tab' && node.label === pestana);
  if (!tabNode) {
    return expanded;
  }

  expanded.push(tabNode.id);

  if (bloque.length > 0) {
    expanded.push(...collectExpandedTestNodeIds(tabNode, bloque, tema));
    return expanded;
  }

  if (navPath.length === 0) {
    return expanded;
  }

  const pathIndices = navPath.split(',').map((part) => Number.parseInt(part, 10))
    .filter((value) => Number.isFinite(value) && value >= 0);

  let nodeLevel = tabNode.children.filter((child) => child.kind === 'contenido-node');
  for (const index of pathIndices) {
    const node = nodeLevel[index];
    if (!node) {
      break;
    }
    expanded.push(node.id);
    nodeLevel = node.children.filter((child) => child.kind === 'contenido-node');
  }

  const archivo = (params.get('archivo') || '').trim();
  if (archivo.length > 0) {
    const fileNode = collectSidebarDescendants(tabNode).find(
      (node) => node.kind === 'contenido-file' && node.label === archivo,
    );
    if (fileNode) {
      expanded.push(fileNode.id);
    }
  }

  return expanded;
}

/**
 * @param {SidebarTreeNode} node
 * @returns {SidebarTreeNode[]}
 */
function collectSidebarDescendants(node) {
  const stack = [...node.children];
  const result = [];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      continue;
    }
    result.push(current);
    stack.push(...current.children);
  }
  return result;
}
